// CIN-7181: Wait or Drop-off choice step helpers.
//
// The wire values ('waiter' / 'drop_off') are the backend's VISIT_TYPES
// (CalendarBookingSerializer.visit_type is a ChoiceField: 'drop-off', 'Waiter'
// and '' are all rejected), so they are never reformatted here. Which types a
// visitor can choose comes ONLY from the availability response's
// slots_by_visit_type keys (a key is absent when the type is not offered and
// present-but-empty when it is offered with no open slots) - the widget never
// re-derives eligibility itself.

export const VISIT_TYPE_WAITER = 'waiter';
export const VISIT_TYPE_DROP_OFF = 'drop_off';
// Canonical order: the choice step and every derived list use it.
export const VISIT_TYPES = [VISIT_TYPE_WAITER, VISIT_TYPE_DROP_OFF];

// Review & Confirm / Success display value.
export const VISIT_TYPE_LABELS = {
  [VISIT_TYPE_WAITER]: 'Wait',
  [VISIT_TYPE_DROP_OFF]: 'Drop-off',
};

// Choice step tap-target copy.
export const VISIT_TYPE_OPTION_LABELS = {
  [VISIT_TYPE_WAITER]: "I'll wait",
  [VISIT_TYPE_DROP_OFF]: "I'll drop off",
};

// CIN-7180: choice card description, under the card title.
export const VISIT_TYPE_OPTION_DESCRIPTIONS = {
  [VISIT_TYPE_WAITER]: 'Stay with us during your appointment.',
  [VISIT_TYPE_DROP_OFF]: "Leave it with us and pick it up when it's ready.",
};

// Kebab-case suffix for data-testid (the wire value keeps its underscore).
export const VISIT_TYPE_TEST_IDS = {
  [VISIT_TYPE_WAITER]: 'waiter',
  [VISIT_TYPE_DROP_OFF]: 'drop-off',
};

export const VISIT_TYPE_STEP_TITLE = 'How would you like to schedule?';
// Backend resolver default (services/dropoff_settings.py) for a location
// without its own pickup promise.
export const DEFAULT_PICKUP_PROMISE = 'Ready by end of day';
export const NO_SHARED_VISIT_TYPE_MESSAGE = "These services can't be booked together. Please change your selection.";
export const DROPOFF_UNAVAILABLE_MESSAGE = 'That drop-off time is no longer available, please pick another.';
// Date & Time notice when Confirm found no visit type and sent the visitor back.
export const CONFIRM_TIME_MESSAGE = 'Please confirm your appointment time.';
// /book rejections that mean "this drop-off time is gone, pick another".
export const DROPOFF_RETRY_CODES = ['DROPOFF_CAPACITY_REACHED', 'DROPOFF_SLOT_UNAVAILABLE'];

export const isVisitType = (value) => VISIT_TYPES.includes(value);

// Offered types, canonical order. [] when the response carries no
// slots_by_visit_type (a flag-off response) or an empty one.
export const offeredVisitTypes = (response) => {
  const byType = response?.slots_by_visit_type;
  if (!byType || typeof byType !== 'object' || Array.isArray(byType)) return [];
  return VISIT_TYPES.filter((type) => Object.prototype.hasOwnProperty.call(byType, type));
};

export const isNoSharedVisitTypeError = (error) =>
  error?.response?.data?.code === 'NO_SHARED_VISIT_TYPE';

// The slot list Date & Time renders: the chosen type's list when the feature is
// on, the legacy available_slots when it is off.
export const slotsFor = (response, enabled, visitType) => {
  if (enabled) {
    const slots = response?.slots_by_visit_type?.[visitType];
    return Array.isArray(slots) ? slots : [];
  }
  return response?.available_slots || [];
};

// The choice step appears only when the feature is on and BOTH types are offered.
export const stepShown = (enabled, offered) => !!enabled && Array.isArray(offered) && offered.length === 2;

// Progress UI position. The choice step has no current_step number of its own
// (it is a sub-state of step 2, so every consumer of current_step keeps its
// meaning); when it is shown it is displayed as step 3 of 6 and every later
// step moves up by one.
export const displayProgress = ({ currentStep, visitTypeStepActive, shown }) => {
  if (!shown) return { step: currentStep, total: 5 };
  let step;
  if (currentStep <= 1) step = currentStep;
  else if (currentStep === 2) step = visitTypeStepActive ? 3 : 2;
  else step = currentStep + 1;
  return { step, total: 6 };
};

// Identifies the availability response the Services -> choice resolution
// fetched, so Date & Time can reuse it instead of fetching again.
export const availabilityKey = (locationId, serviceIds) => `${locationId}|${(serviceIds || []).join(',')}`;
